import { motion } from "framer-motion";
import { Reveal, SectionHead } from "@/components/Reveal";
import { CandlestickChart, ShieldCheck, Gauge } from "lucide-react";

const IMG =
  "https://images.pexels.com/photos/5831259/pexels-photo-5831259.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const CARDS = [
  {
    icon: CandlestickChart,
    title: "Exchange Execution",
    body: "Active hedging and position management across global metal exchanges, with arbitrage and spread tracking between physical and paper markets.",
    items: ["London Metal Exchange (LME)", "COMEX (CME Group)", "Shanghai Metal Exchange (SME)"],
  },
  {
    icon: ShieldCheck,
    title: "Hedging & Structure Strategy",
    body: "Tailored financial structures to lock in profit margins and mitigate volatility across delivery timelines.",
    items: ["Zero-Cost Collars for downside protection", "OTC swaps matched to physical delivery", "Options for inventory valuation protection", "Fixation schedule optimization"],
  },
  {
    icon: Gauge,
    title: "P&L & Exposure Tracking",
    body: "Robust financial controls delivering real-time risk visibility across the entire book.",
    items: ["Mark-to-Market (MTM) P&L models", "Physical exposure & order book reconciliation", "Currency risk on cross-border transactions", "Counterparty credit exposure analysis"],
  },
];

const EXCHANGES = ["LME", "COMEX", "SME"];

export default function Derivatives() {
  return (
    <section id="derivatives" data-testid="derivatives-section" className="py-20 lg:py-32">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-8 lg:px-16">
        <SectionHead
          index="/03"
          overline="Risk Management & Derivatives"
          title="Protecting physical metal margins through structured hedging and multi-exchange risk tracking."
          testId="derivatives-head"
        />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
          <div className="lg:col-span-7 flex flex-col gap-6 order-2 lg:order-1">
            {CARDS.map((c, i) => (
              <Reveal key={c.title} delay={i * 0.08}>
                <div data-testid={`derivatives-card-${i}`} className="card-industrial p-7 lg:p-9 group">
                  <div className="flex items-start gap-5">
                    <span className="shrink-0 w-11 h-11 rounded-sm bg-[#E06D28]/10 border border-[#E06D28]/30 flex items-center justify-center text-[#F07D38] transition-transform duration-300 group-hover:scale-110">
                      <c.icon size={20} />
                    </span>
                    <div>
                      <h3 className="font-display text-lg sm:text-xl font-bold text-[#F4F6F8]">{c.title}</h3>
                      <p className="text-sm text-[#94A3B8] mt-2 leading-relaxed">{c.body}</p>
                      <ul className="mt-4 grid sm:grid-cols-2 gap-x-6 gap-y-2">
                        {c.items.map((item) => (
                          <li key={item} className="flex items-start gap-2 text-sm text-[#94A3B8]">
                            <span className="mt-[7px] w-1.5 h-1.5 rotate-45 bg-[#E06D28] shrink-0" />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal className="lg:col-span-5 order-1 lg:order-2">
            <motion.div
              data-testid="derivatives-image-frame"
              whileHover={{ scale: 0.985 }}
              transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              className="relative overflow-hidden rounded-md border border-[#283244] img-spotlight lg:sticky lg:top-28"
            >
              <img src={IMG} alt="Financial trading desk with market charts" className="w-full h-[420px] lg:h-[620px] object-cover" loading="lazy" />
              <div className="absolute bottom-0 left-0 right-0 p-6 z-10">
                <div className="flex gap-2 mb-3" data-testid="exchange-chips">
                  {EXCHANGES.map((e) => (
                    <span key={e} className="font-mono-tech text-[11px] font-semibold tracking-[0.15em] text-[#0A0D12] bg-[#F3B700] px-3 py-1 rounded-sm">
                      {e}
                    </span>
                  ))}
                </div>
                <div className="font-display text-lg font-bold text-[#F4F6F8]">Paper precision for physical exposure</div>
              </div>
              <span className="absolute bottom-0 left-0 w-10 h-10 border-b-2 border-l-2 border-[#E06D28] z-10" />
              <span className="absolute bottom-0 right-0 w-10 h-10 border-b-2 border-r-2 border-[#E06D28] z-10" />
            </motion.div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
